import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeAdditions } from '@/types/menuAdditions';
import { splitItemChoices } from '@/utils/orderItemName';

export interface ContractedChoice {
  name: string;
  detail?: string | null;
  count: number;
}

export interface ContractedGroup {
  id: string;
  label: string;
  choices: ContractedChoice[];
}

export interface ContractedItem {
  title: string;
  quantity: number;
  value: number;
  groups: ContractedGroup[];
  /** Escolhas de menus por seleção (ex.: sabores do combo). */
  choices: ContractedChoice[];
  substitutions: string[];
  additions: string[];
  optionalIds: string[];
  productId?: string;
}

const humanize = (id: string) => {
  const s = id.replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : id;
};

/** "Calabresa (Molho de tomate, calabresa)" → { name, detail } */
const splitNameDetail = (raw: string): { name: string; detail: string | null } => {
  const m = /^(.*?)\s*\(\s*(.*)\)\s*$/.exec(String(raw).trim());
  return m && m[1] ? { name: m[1].trim(), detail: m[2].trim() || null } : { name: String(raw).trim(), detail: null };
};

const countChoices = (names: string[]): ContractedChoice[] => {
  const map = new Map<string, ContractedChoice>();
  names.forEach((raw) => {
    const { name, detail } = splitNameDetail(raw);
    const key = `${name}|${detail ?? ''}`;
    const cur = map.get(key);
    if (cur) cur.count += 1;
    else map.set(key, { name, detail, count: 1 });
  });
  return Array.from(map.values());
};

const toArray = (value: any): any[] => {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return []; }
  }
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Object.values(v);
  return [];
};

/** Converte uma entrada de carrinho/menu (cart_items, menu ou item do carrinho) em item contratado detalhado. */
export function parseContractedEntry(e: any, catLabels: Record<string, string> = {}): ContractedItem {
  const rawName = String(e?.name ?? e?.productName ?? e?.menu ?? 'Item');
  const quantity = Number(e?.quantity) > 0 ? Number(e.quantity) : 1;
  const value = Number(e?.totalPrice ?? e?.total_price ?? e?.price ?? 0) || 0;
  const baseTitle = rawName.replace(/^\d+x\s*/i, '');

  const groups: ContractedGroup[] = [];
  const selections = e?.categorySelections;
  if (selections && typeof selections === 'object') {
    Object.entries(selections as Record<string, unknown>).forEach(([catId, list]) => {
      const names = (Array.isArray(list) ? list : []).map(String).filter(Boolean);
      if (names.length === 0) return;
      groups.push({ id: catId, label: catLabels[catId] || humanize(catId), choices: countChoices(names) });
    });
  }

  // Ordem do cardápio do fornecedor: as chaves de categorySelections vêm reordenadas pelo banco (jsonb ordena por
  // tamanho/alfabeto), então segue a ordem das categorias cadastradas no cardápio (catLabels preserva essa ordem).
  const catOrder = Object.keys(catLabels);
  if (catOrder.length > 0 && groups.length > 1) {
    const pos = (id: string) => { const i = catOrder.indexOf(id); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
    groups.sort((a, b) => pos(a.id) - pos(b.id));
  }

  // Menus por seleção: sabores/itens escolhidos
  let choices: ContractedChoice[] = [];
  let title = baseTitle;
  if (groups.length === 0) {
    let names: string[] = [];
    if (e?.selectedItemNames && typeof e.selectedItemNames === 'object') {
      names = Object.values(e.selectedItemNames as Record<string, unknown>).map(String);
    } else if (e?.isSelectableMenu && typeof e?.itemName === 'string' && e.itemName.trim()) {
      names = e.itemName.split(' • ').map((s: string) => s.replace(/^\d+x\s*/, '').trim());
    }
    // "Combo (a, b, c)" → título + lista de escolhas
    const split = splitItemChoices(baseTitle);
    if (split.choices.length > 0) {
      title = split.title;
      choices = countChoices(split.choices);
    } else if (names.length > 0) {
      const flat = names.flatMap((n) => {
        const sp = splitItemChoices(n);
        return sp.choices.length > 0 ? sp.choices : [n];
      });
      // evita repetir o próprio título como "escolha"
      const filtered = flat.filter((n) => n.trim().toLowerCase() !== baseTitle.trim().toLowerCase());
      choices = countChoices(filtered);
    }
  }

  const substitutions: string[] = [];
  const subs = e?.substitutions;
  if (subs && typeof subs === 'object') {
    Object.entries(subs as Record<string, any>).forEach(([orig, s]) => {
      if (s?.substituteItem) substitutions.push(`${s.originalItem || orig} → ${s.substituteItem}`);
    });
  }

  const additions: string[] = [];
  const addMap = normalizeAdditions(e?.additions);
  Object.entries(addMap).forEach(([catId, list]) => {
    list.forEach((a) => additions.push(`${a.name}${catLabels[catId] ? ` (${catLabels[catId]})` : ''}`));
  });

  const optionalIds = (Array.isArray(e?.selectedOptionalItems) ? e.selectedOptionalItems : [])
    .map((o: any) => (typeof o === 'string' ? o : o?.id))
    .filter(Boolean) as string[];

  return { title, quantity, value, groups, choices, substitutions, additions, optionalIds, productId: e?.productId };
}

/**
 * Itens contratados com TODOS os detalhes (categorias na ordem do fornecedor, sabores, trocas,
 * itens incluídos e adicionais com nome). Busca no cardápio do fornecedor os nomes das categorias e dos adicionais.
 */
export function useContractedItems(rawEntries: unknown): { items: ContractedItem[]; optionalNames: Record<string, string> } {
  const entries = useMemo(() => toArray(rawEntries).filter((e) => e && typeof e === 'object'), [rawEntries]);
  const [catLabelsByProduct, setCatLabelsByProduct] = useState<Record<string, Record<string, string>>>({});
  const [optionalNames, setOptionalNames] = useState<Record<string, string>>({});

  const productIds = useMemo(
    () => Array.from(new Set(entries.map((e: any) => e?.productId).filter(Boolean))) as string[],
    [entries],
  );
  const optionalIdList = useMemo(
    () => Array.from(new Set(entries.flatMap((e: any) => (Array.isArray(e?.selectedOptionalItems) ? e.selectedOptionalItems : []))
      .map((o: any) => (typeof o === 'string' ? o : o?.id)).filter(Boolean))) as string[],
    [entries],
  );

  useEffect(() => {
    if (productIds.length === 0) { setCatLabelsByProduct({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any).from('supplier_menu').select('id, menu_description').in('id', productIds);
        if (cancelled || !data) return;
        const next: Record<string, Record<string, string>> = {};
        data.forEach((row: any) => {
          let desc: any = row.menu_description;
          if (typeof desc === 'string') { try { desc = JSON.parse(desc); } catch { desc = null; } }
          if (Array.isArray(desc)) {
            next[row.id] = {};
            desc.forEach((c: any) => { if (c?.id && c?.label) next[row.id][String(c.id)] = String(c.label); });
          }
        });
        setCatLabelsByProduct(next);
      } catch { /* sem rótulos: usa o id humanizado */ }
    })();
    return () => { cancelled = true; };
  }, [productIds.join('|')]);

  useEffect(() => {
    if (optionalIdList.length === 0) { setOptionalNames({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any).from('supplier_menu').select('id, name, item_name').in('id', optionalIdList);
        if (cancelled || !data) return;
        const map: Record<string, string> = {};
        data.forEach((r: any) => { map[r.id] = r.name || r.item_name || 'Adicional'; });
        setOptionalNames(map);
      } catch { /* sem nomes */ }
    })();
    return () => { cancelled = true; };
  }, [optionalIdList.join('|')]);

  const items = useMemo(
    () => entries.map((e: any) => parseContractedEntry(e, catLabelsByProduct[e?.productId] || {})),
    [entries, catLabelsByProduct],
  );

  return { items, optionalNames };
}
